import { useState, useEffect, lazy, Suspense } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import { fetchAdminMetrics, fetchPrograms } from "../lib/sentinel-api";
import { supabase } from "../lib/supabase";
import type { CertificationProgram } from "../lib/certifications";
import type { AdminMetricsResponse } from "../lib/sentinel-api";

const ContentManager = lazy(() => import("../components/ContentManager").then(m => ({ default: m.ContentManager })));

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  organization_id: string | null;
  department_id: string | null;
  active_licenses: string[];
  completed_certifications: string[];
  created_at: string;
}

interface AdminOrg {
  id: string;
  name: string;
  type: string;
  ein: string | null;
  industry: string | null;
  created_at: string;
}

interface AdminLicense {
  id: string;
  type: string;
  name: string;
  seats: number;
  industry: string;
  status: string;
  price: number;
  currency: string;
  organization_id: string | null;
  start_date: string;
  end_date: string;
}

interface AdminCertStatus {
  id: string;
  user_id: string;
  program_id: string;
  status: string;
  exam_score: number | null;
  certificate_issued: boolean;
  badge_issued: boolean;
  completion_date: string | null;
  renewal_due_date: string | null;
  credential_code: string | null;
}

interface AdminDepartment {
  id: string;
  organization_id: string;
  name: string;
  budget: number | null;
}

interface ContactSubmission {
  id: string;
  name: string;
  organization: string | null;
  email: string;
  industry: string | null;
  domain: string | null;
  message: string;
  created_at: string;
}

interface LicensingQuote {
  id: string;
  org_name: string;
  contact_name: string;
  email: string;
  industry: string | null;
  selected_tiers: string[];
  status: string;
  created_at: string;
}

function MetricCard({ label, value, sublabel }: { label: string; value: string; sublabel?: string }) {
  return (
    <div className="card" style={{ padding: "var(--space-4)" }}>
      <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)", marginBottom: "0.5rem" }}>
        {label}
      </div>
      <div style={{ fontSize: "2rem", fontWeight: 800, marginBottom: "0.25rem" }}>{value}</div>
      {sublabel && <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{sublabel}</div>}
    </div>
  );
}

function StripeWebhookWizard() {
  const [step, setStep] = useState(1);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
    if (supabaseUrl) {
      setWebhookUrl(`${supabaseUrl}/functions/v1/stripe-webhook`);
    }
  }, []);

  function copyUrl() {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ maxWidth: "640px" }}>
      <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Stripe Webhook Setup Wizard</h2>
      <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
        Connect your Stripe account to enable automated payment processing, checkout, and subscription management for all certification programs.
      </p>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
        {[1, 2, 3, 4].map((s) => (
          <div key={s} style={{ flex: 1 }}>
            <div style={{ height: "4px", borderRadius: "var(--radius-full)", background: step >= s ? "var(--accent-500)" : "var(--neutral-200)", transition: "background var(--transition-fast)" }} />
            <div style={{ fontSize: "0.6875rem", fontWeight: 600, color: step >= s ? "var(--accent-600)" : "var(--text-muted)", marginTop: "0.25rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Step {s}
            </div>
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Step 1: Copy Your Webhook URL</h3>
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "var(--space-3)" }}>
            This is your unique Stripe webhook endpoint. Stripe will send payment events to this URL to automatically process enrollments, renewals, and seat assignments.
          </p>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "var(--space-3)" }}>
            <input
              readOnly
              value={webhookUrl}
              className="form-input"
              style={{ flex: 1, fontFamily: "var(--font-mono)", fontSize: "0.8125rem" }}
            />
            <button onClick={copyUrl} className="btn btn-secondary" style={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <button onClick={() => setStep(2)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Continue</button>
        </div>
      )}

      {step === 2 && (
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Step 2: Create Webhook in Stripe Dashboard</h3>
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "var(--space-3)" }}>
            Go to the Stripe Dashboard and create a new webhook endpoint:
          </p>
          <ol style={{ fontSize: "0.8125rem", lineHeight: 1.8, paddingLeft: "1.25rem", marginBottom: "var(--space-3)" }}>
            <li>Navigate to Developers &rarr; Webhooks in your Stripe Dashboard</li>
            <li>Click "Add endpoint"</li>
            <li>Paste your webhook URL (copied in Step 1)</li>
            <li>Select the following events to listen for:</li>
          </ol>
          <div style={{ padding: "0.75rem", borderRadius: "var(--radius-md)", background: "var(--bg-subtle)", marginBottom: "var(--space-3)" }}>
            <ul style={{ fontSize: "0.75rem", lineHeight: 1.6, paddingLeft: "1.25rem", margin: 0, fontFamily: "var(--font-mono)" }}>
              <li>checkout.session.completed</li>
              <li>customer.subscription.created</li>
              <li>customer.subscription.updated</li>
              <li>customer.subscription.deleted</li>
              <li>invoice.payment_succeeded</li>
              <li>invoice.payment_failed</li>
            </ul>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={() => setStep(1)} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Back</button>
            <button onClick={() => setStep(3)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Continue</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Step 3: Copy Your Signing Secret</h3>
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "var(--space-3)" }}>
            After creating the webhook, Stripe will show you a signing secret (starts with <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.75rem" }}>whsec_</code>).
            This secret is already configured in your Supabase project — no action needed. The webhook will automatically verify all incoming events.
          </p>
          <div className="card" style={{ padding: "0.75rem", background: "var(--accent-50)", borderColor: "var(--accent-300)", marginBottom: "var(--space-3)" }}>
            <div style={{ fontSize: "0.8125rem", color: "var(--accent-700)", fontWeight: 600 }}>
              &#10003; Webhook signing secret is pre-configured
            </div>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
              Your Stripe webhook secret is stored securely in your Supabase Edge Function environment. All incoming webhook events are automatically verified.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={() => setStep(2)} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Back</button>
            <button onClick={() => setStep(4)} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Continue</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>Step 4: Test Your Webhook</h3>
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "var(--space-3)" }}>
            Send a test event from the Stripe Dashboard to verify your webhook is receiving events correctly.
          </p>
          <ol style={{ fontSize: "0.8125rem", lineHeight: 1.8, paddingLeft: "1.25rem", marginBottom: "var(--space-3)" }}>
            <li>In the Stripe Dashboard, go to your webhook endpoint</li>
            <li>Click "Send test webhook"</li>
            <li>Select the <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.75rem" }}>checkout.session.completed</code> event</li>
            <li>Click "Send test webhook"</li>
            <li>Check that the event shows a 200 OK response</li>
          </ol>
          <div className="card" style={{ padding: "0.75rem", background: "var(--accent-50)", borderColor: "var(--accent-300)", marginBottom: "var(--space-3)" }}>
            <div style={{ fontSize: "0.8125rem", color: "var(--accent-700)", fontWeight: 600 }}>
              &#10003; Setup Complete
            </div>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
              Once you see a 200 OK response in Stripe, your webhook is live. All payment events will be automatically processed — enrollments, renewals, seat assignments, and certificate issuance.
            </p>
          </div>
          <button onClick={() => setStep(1)} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Start Over</button>
        </div>
      )}
    </div>
  );
}

interface SeatImportRow {
  name: string;
  email: string;
  licenseId: string;
  status: "pending" | "assigned" | "error";
  message?: string;
}

function BulkSeatManager() {
  const { identity } = useIdentity();
  const [csvText, setCsvText] = useState("");
  const [parsedRows, setParsedRows] = useState<SeatImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [licenses, setLicenses] = useState<{ id: string; name: string; industry: string; seats: number }[]>([]);

  useEffect(() => {
    const orgId = identity.organizationId ?? "";
    if (orgId) {
      import("../lib/sentinel-api").then(({ fetchOrgSeats }) => {
        fetchOrgSeats(orgId).then((data) => {
          if (data.licenses) {
            setLicenses(data.licenses.map((l) => ({ id: l.id, name: l.name, industry: l.industry, seats: l.seats })));
          }
        });
      });
    }
  }, [identity.organizationId]);

  function parseCsv() {
    const lines = csvText.trim().split("\n");
    const rows: SeatImportRow[] = [];
    for (const line of lines) {
      const parts = line.split(",").map((p) => p.trim());
      if (parts.length < 3) continue;
      const [name, email, licenseId] = parts;
      if (!name || !email || !licenseId) continue;
      rows.push({ name, email, licenseId, status: "pending" });
    }
    setParsedRows(rows);
    setImportResult(null);
  }

  async function handleImport() {
    setImporting(true);
    let success = 0;
    let errors = 0;
    for (const row of parsedRows) {
      try {
        const { assignSeat } = await import("../lib/sentinel-api");
        await assignSeat(row.licenseId, row.email, identity.userId ?? undefined);
        row.status = "assigned";
        success++;
      } catch {
        row.status = "error";
        row.message = "Assignment failed";
        errors++;
      }
      setParsedRows([...parsedRows]);
    }
    setImporting(false);
    setImportResult(`Import complete: ${success} seats assigned, ${errors} errors.`);
  }

  function handleExport() {
    if (parsedRows.length === 0) return;
    const csv = ["Name,Email,License ID,Status"];
    for (const row of parsedRows) {
      csv.push(`${row.name},${row.email},${row.licenseId},${row.status}`);
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "seat-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function downloadTemplate() {
    const template = "Name,Email,License ID\nJohn Smith,john@example.com,license-law-enforcement-agency\nJane Doe,jane@example.com,license-healthcare-organization";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "seat-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ maxWidth: "800px" }}>
      <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Bulk Seat Import / Export</h2>
      <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
        Import or export seat assignments in bulk using CSV format. Perfect for onboarding entire teams or migrating from another system.
      </p>

      {licenses.length > 0 && (
        <div className="card" style={{ padding: "var(--space-3)", marginBottom: "var(--space-3)" }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", marginBottom: "0.375rem" }}>Available Licenses</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {licenses.map((l) => (
              <div key={l.id} style={{ fontSize: "0.8125rem", display: "flex", justifyContent: "space-between" }}>
                <span>{l.name} ({l.industry})</span>
                <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.75rem", color: "var(--text-muted)" }}>{l.id}</code>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
          <h3 style={{ fontSize: "0.9375rem", fontWeight: 700 }}>CSV Input</h3>
          <button onClick={downloadTemplate} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}>Download Template</button>
        </div>
        <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: "0.5rem" }}>
          Paste CSV data with columns: Name, Email, License ID. One seat per line.
        </p>
        <textarea
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          className="form-input"
          style={{ width: "100%", minHeight: "120px", fontFamily: "var(--font-mono)", fontSize: "0.8125rem", marginBottom: "0.75rem" }}
          placeholder="Name,Email,License ID&#10;John Smith,john@example.com,license-law-enforcement-agency"
        />
        <button onClick={parseCsv} className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>Parse CSV</button>
      </div>

      {parsedRows.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: "var(--space-3)" }}>
          <div style={{ padding: "var(--space-3)", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontSize: "0.9375rem", fontWeight: 700 }}>Parsed Rows ({parsedRows.length})</h3>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button onClick={handleExport} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}>Export CSV</button>
              <button onClick={handleImport} disabled={importing} className="btn btn-primary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem" }}>
                {importing ? "Importing..." : "Import All"}
              </button>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  {["Name", "Email", "License ID", "Status"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.8125rem", fontWeight: 600 }}>{row.name}</td>
                    <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{row.email}</td>
                    <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>{row.licenseId}</td>
                    <td style={{ padding: "0.5rem 0.75rem" }}>
                      {row.status === "pending" && <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--neutral-100)", color: "var(--text-muted)", fontWeight: 600 }}>Pending</span>}
                      {row.status === "assigned" && <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--accent-50)", color: "var(--accent-600)", fontWeight: 600 }}>&#10003; Assigned</span>}
                      {row.status === "error" && <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--error-50)", color: "var(--error-600)", fontWeight: 600 }}>&#10007; Error</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importResult && (
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)", borderColor: "var(--accent-400)", background: "var(--accent-50)", fontSize: "0.875rem", fontWeight: 600, color: "var(--accent-700)" }}>
          {importResult}
        </div>
      )}
    </div>
  );
}

const ROLE_COLORS: Record<string, string> = {
  platform_admin: "#dc2626",
  enterprise_admin: "#2563eb",
  agency_admin: "#0891b2",
  manager: "#059669",
  supervisor: "#d97706",
  student: "#6b7280",
  executive: "#7c3aed",
};

function RoleBadge({ role }: { role: string }) {
  const color = ROLE_COLORS[role] ?? "#6b7280";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.125rem 0.5rem",
      borderRadius: "var(--radius-sm)",
      fontSize: "0.6875rem",
      fontWeight: 700,
      background: `${color}15`,
      color,
      textTransform: "capitalize",
    }}>
      {role.replace(/_/g, " ")}
    </span>
  );
}

export default function AdminPage() {
  const { identity } = useIdentity();
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "organizations" | "licenses" | "certifications" | "programs" | "stripe" | "seats" | "emails" | "submissions" | "content">("overview");
  const [metrics, setMetrics] = useState<AdminMetricsResponse["metrics"] | null>(null);
  const [programs, setPrograms] = useState<CertificationProgram[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [licenses, setLicenses] = useState<AdminLicense[]>([]);
  const [certStatuses, setCertStatuses] = useState<AdminCertStatus[]>([]);
  const [departments, setDepartments] = useState<AdminDepartment[]>([]);
  const [contactSubmissions, setContactSubmissions] = useState<ContactSubmission[]>([]);
  const [licensingQuotes, setLicensingQuotes] = useState<LicensingQuote[]>([]);
  const [emails, setEmails] = useState<Array<{ id: string; recipient_email: string; subject: string; body: string; email_type: string; program_id: string | null; amount: number | null; currency: string | null; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    fetchAdminMetrics().then((res) => {
      if (res?.metrics) setMetrics(res.metrics);
    });
    fetchPrograms().then((p) => {
      setPrograms(p);
      setLoading(false);
    });

    // Fetch all users
    supabase
      .from("sentinel_users")
      .select("id, name, email, role, organization_id, department_id, active_licenses, completed_certifications, created_at")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setUsers(data as AdminUser[]);
      });

    // Fetch all organizations
    supabase
      .from("sentinel_organizations")
      .select("id, name, type, ein, industry, created_at")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setOrgs(data as AdminOrg[]);
      });

    // Fetch all licenses
    supabase
      .from("sentinel_licenses")
      .select("id, type, name, seats, industry, status, price, currency, organization_id, start_date, end_date")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setLicenses(data as AdminLicense[]);
      });

    // Fetch all certification statuses
    supabase
      .from("sentinel_certification_status")
      .select("id, user_id, program_id, status, exam_score, certificate_issued, badge_issued, completion_date, renewal_due_date, credential_code")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setCertStatuses(data as AdminCertStatus[]);
      });

    // Fetch all departments
    supabase
      .from("sentinel_departments")
      .select("id, organization_id, name, budget")
      .order("name", { ascending: true })
      .then(({ data }) => {
        if (data) setDepartments(data as AdminDepartment[]);
      });

    // Fetch email log (admin only — RLS requires platform_admin)
    supabase
      .from("sentinel_email_log")
      .select("id, recipient_email, subject, body, email_type, program_id, amount, currency, created_at")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => {
        if (data) setEmails(data as typeof emails);
      });

    // Fetch contact form submissions
    supabase
      .from("sentinel_contact_submissions")
      .select("id, name, organization, email, industry, domain, message, created_at")
      .order("created_at", { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (data) setContactSubmissions(data as ContactSubmission[]);
      });

    // Fetch licensing quote requests
    supabase
      .from("sentinel_licensing_quotes")
      .select("id, org_name, contact_name, email, industry, selected_tiers, status, created_at")
      .order("created_at", { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (data) setLicensingQuotes(data as LicensingQuote[]);
      });
  }, []);

  const seatUtilization = metrics?.seat_utilization_by_industry ?? {};
  const filteredUsers = userSearch
    ? users.filter((u) =>
        u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.role.toLowerCase().includes(userSearch.toLowerCase())
      )
    : users;

  const orgNameById = (orgId: string | null) => orgs.find((o) => o.id === orgId)?.name ?? "—";
  const deptNameById = (deptId: string | null) => departments.find((d) => d.id === deptId)?.name ?? "—";
  const userNameById = (userId: string) => users.find((u) => u.id === userId)?.name ?? "Unknown";
  const userEmailById = (userId: string) => users.find((u) => u.id === userId)?.email ?? "—";

  return (
    <PlatformLayout>
      {/* Hero */}
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Platform Admin Console</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Full platform oversight of Sentinel certifications, licenses, organizations, training programs, and user accounts.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.25rem", marginBottom: "var(--space-4)", borderBottom: "2px solid var(--border)", flexWrap: "wrap" }}>
        {([
          { key: "overview", label: "Overview" },
          { key: "users", label: "Users" },
          { key: "organizations", label: "Organizations" },
          { key: "licenses", label: "Licenses" },
          { key: "certifications", label: "Certifications" },
          { key: "programs", label: "Programs" },
          { key: "stripe", label: "Stripe Setup" },
          { key: "seats", label: "Bulk Seats" },
          { key: "emails", label: "Email Log" },
          { key: "submissions", label: "Submissions" },
          { key: "content", label: "Content" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "0.625rem 1rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              background: "transparent",
              border: "none",
              borderBottom: activeTab === tab.key ? "2px solid var(--accent-500)" : "2px solid transparent",
              color: activeTab === tab.key ? "var(--accent-600)" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all var(--transition-fast)",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
            <MetricCard label="Total Users" value={String(users.length)} sublabel="All registered members" />
            <MetricCard label="Organizations" value={String(orgs.length)} sublabel="Registered companies" />
            <MetricCard label="Active Licenses" value={String(licenses.filter((l) => l.status === "active").length)} sublabel={`of ${licenses.length} total`} />
            <MetricCard label="Certifications" value={String(certStatuses.length)} sublabel={`${certStatuses.filter((c) => c.status === "completed").length} completed`} />
            <MetricCard label="Training Programs" value={String(programs.length)} sublabel="Across all industries" />
            <MetricCard label="Departments" value={String(departments.length)} sublabel="Across all organizations" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "var(--space-4)" }}>
            {/* Users by Role */}
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Users by Role</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {["platform_admin", "enterprise_admin", "agency_admin", "manager", "supervisor", "student"].map((role) => {
                  const count = users.filter((u) => u.role === role).length;
                  const pct = users.length > 0 ? (count / users.length) * 100 : 0;
                  return (
                    <div key={role}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                        <span style={{ fontSize: "0.875rem", fontWeight: 500, textTransform: "capitalize" }}>{role.replace(/_/g, " ")}</span>
                        <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{count}</span>
                      </div>
                      <div style={{ width: "100%", height: "8px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: ROLE_COLORS[role] ?? "#6b7280", borderRadius: "var(--radius-full)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Seat Utilization */}
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Seat Utilization by Industry</h2>
              {Object.keys(seatUtilization).length === 0 ? (
                <p className="text-muted" style={{ fontSize: "0.875rem" }}>No seat data available yet.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {Object.entries(seatUtilization).map(([industry, data]) => {
                    const pct = data.total > 0 ? (data.assigned / data.total) * 100 : 0;
                    return (
                      <div key={industry}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{industry}</span>
                          <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{data.assigned} / {data.total}</span>
                        </div>
                        <div style={{ width: "100%", height: "8px", background: "var(--neutral-200)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: pct > 80 ? "var(--warning-500)" : "linear-gradient(90deg, var(--accent-500), var(--accent-400))", borderRadius: "var(--radius-full)" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Users */}
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Recent Users</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
                {users.slice(0, 6).map((u) => (
                  <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
                    <div>
                      <div style={{ fontSize: "0.875rem", fontWeight: 600 }}>{u.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{u.email}</div>
                    </div>
                    <RoleBadge role={u.role} />
                  </div>
                ))}
                {users.length === 0 && <p className="text-muted" style={{ fontSize: "0.875rem" }}>No users yet.</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Users Tab */}
      {activeTab === "users" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
            <h2 style={{ fontSize: "1.125rem" }}>All Users ({users.length})</h2>
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="form-input"
              style={{ maxWidth: "300px", fontSize: "0.8125rem" }}
            />
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Name", "Email", "Role", "Organization", "Department", "Licenses", "Certs"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{u.name}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{u.email}</td>
                      <td style={{ padding: "0.625rem 1rem" }}><RoleBadge role={u.role} /></td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{orgNameById(u.organization_id)}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{deptNameById(u.department_id)}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{u.active_licenses?.length ?? 0}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{u.completed_certifications?.length ?? 0}</td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Organizations Tab */}
      {activeTab === "organizations" && (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Organizations ({orgs.length})</h2>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Name", "Type", "EIN", "Industry", "Users", "Licenses", "Departments"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => {
                    const orgUsers = users.filter((u) => u.organization_id === o.id);
                    const orgLicenses = licenses.filter((l) => l.organization_id === o.id);
                    const orgDepts = departments.filter((d) => d.organization_id === o.id);
                    return (
                      <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{o.name}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{o.type?.replace(/_/g, " ") ?? "—"}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>{o.ein ?? "—"}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{o.industry ?? "—"}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{orgUsers.length}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{orgLicenses.length}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{orgDepts.length}</td>
                      </tr>
                    );
                  })}
                  {orgs.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No organizations yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Licenses Tab */}
      {activeTab === "licenses" && (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>All Licenses ({licenses.length})</h2>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["License Name", "Type", "Industry", "Seats", "Status", "Organization", "Price", "Term"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((l) => (
                    <tr key={l.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{l.name}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", textTransform: "capitalize" }}>{l.type}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{l.industry}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{l.seats}</td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: l.status === "active" ? "var(--accent-50)" : "var(--error-50)", color: l.status === "active" ? "var(--accent-600)" : "var(--error-600)", fontWeight: 600 }}>
                          {l.status}
                        </span>
                      </td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{orgNameById(l.organization_id)}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{l.currency}{l.price}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{l.start_date} → {l.end_date}</td>
                    </tr>
                  ))}
                  {licenses.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No licenses yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Certifications Tab */}
      {activeTab === "certifications" && (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Certification Status ({certStatuses.length})</h2>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["User", "Email", "Program", "Status", "Score", "Cert", "Badge", "Completed", "Renewal Due"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {certStatuses.map((c) => (
                    <tr key={c.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{userNameById(c.user_id)}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{userEmailById(c.user_id)}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontFamily: "var(--font-mono)" }}>{c.program_id}</td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: c.status === "completed" ? "var(--accent-50)" : c.status === "in_progress" ? "var(--warning-50)" : "var(--neutral-100)", color: c.status === "completed" ? "var(--accent-600)" : c.status === "in_progress" ? "var(--warning-600)" : "var(--text-muted)", fontWeight: 600 }}>
                          {c.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{c.exam_score ?? "—"}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{c.certificate_issued ? "✓" : "—"}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{c.badge_issued ? "✓" : "—"}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{c.completion_date ?? "—"}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{c.renewal_due_date ?? "—"}</td>
                    </tr>
                  ))}
                  {certStatuses.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No certification records yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Programs Tab */}
      {activeTab === "programs" && (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Certification Programs ({programs.length})</h2>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Title", "Industry", "Level", "Code", "Price", "Renewal", "Modules"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {programs.map((p) => (
                    <tr key={p.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{p.title}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{p.industry}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{p.level}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>{p.code}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{p.currency}{p.price}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{p.currency}{p.renewal_price}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{p.modules.length} modules</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Stripe Setup Tab */}
      {activeTab === "stripe" && <StripeWebhookWizard />}

      {/* Bulk Seats Tab */}
      {activeTab === "seats" && <BulkSeatManager />}

      {/* Email Log Tab */}
      {activeTab === "emails" && (
        <>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Simulated Email Log ({emails.length})</h2>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>All test-mode purchase confirmation emails are logged here. In production, these would be sent to the purchaser. In test mode, they are captured for your review.</p>
          {emails.length === 0 ? (
            <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
              <p className="text-muted" style={{ fontSize: "0.875rem" }}>No emails logged yet. Purchase a certification with a test account to see the email here.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              {emails.map((email) => (
                <div key={email.id} className="card" style={{ padding: "var(--space-4)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                    <div>
                      <div style={{ fontSize: "0.9375rem", fontWeight: 700, marginBottom: "0.25rem" }}>{email.subject}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>To: {email.recipient_email}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--accent-50)", color: "var(--accent-600)", fontWeight: 600 }}>{email.email_type.replace(/_/g, " ")}</span>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>{new Date(email.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  {email.amount != null && (
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Amount: {email.currency}{email.amount}</div>
                  )}
                  <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.8125rem", lineHeight: 1.6, color: "var(--text-muted)", background: "var(--bg-subtle)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", margin: 0 }}>{email.body}</pre>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Submissions Tab */}
      {activeTab === "submissions" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
            <h2 style={{ fontSize: "1.125rem" }}>Website Submissions</h2>
            <a href="/admin/analytics" className="btn btn-secondary" style={{ fontSize: "0.8125rem" }}>View SEO Analytics &rarr;</a>
          </div>

          {/* Contact Form Submissions */}
          <div style={{ marginBottom: "var(--space-5)" }}>
            <h3 style={{ fontSize: "1rem", marginBottom: "var(--space-2)" }}>Contact Form Submissions ({contactSubmissions.length})</h3>
            {contactSubmissions.length === 0 ? (
              <div className="card" style={{ padding: "var(--space-4)", textAlign: "center" }}>
                <p className="text-muted" style={{ fontSize: "0.875rem" }}>No contact form submissions yet.</p>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid var(--border)" }}>
                        {["Name", "Email", "Organization", "Industry", "Domain", "Date"].map((h) => (
                          <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {contactSubmissions.map((s) => (
                        <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{s.name}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{s.email}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{s.organization ?? "—"}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{s.industry ?? "—"}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{s.domain ?? "—"}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>{new Date(s.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Licensing Quote Requests */}
          <div>
            <h3 style={{ fontSize: "1rem", marginBottom: "var(--space-2)" }}>Licensing Intake Requests ({licensingQuotes.length})</h3>
            {licensingQuotes.length === 0 ? (
              <div className="card" style={{ padding: "var(--space-4)", textAlign: "center" }}>
                <p className="text-muted" style={{ fontSize: "0.875rem" }}>No licensing quote requests yet.</p>
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid var(--border)" }}>
                        {["Organization", "Contact", "Email", "Industry", "Tiers", "Status", "Date"].map((h) => (
                          <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {licensingQuotes.map((q) => (
                        <tr key={q.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{q.org_name}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{q.contact_name}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{q.email}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{q.industry ?? "—"}</td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>{(q.selected_tiers ?? []).join(", ")}</td>
                          <td style={{ padding: "0.625rem 1rem" }}>
                            <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--accent-50)", color: "var(--accent-600)", fontWeight: 600, textTransform: "capitalize" }}>{q.status ?? "pending"}</span>
                          </td>
                          <td style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>{new Date(q.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Content Tab */}
      {activeTab === "content" && (
        <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>Loading content manager...</div>}>
          <ContentManager />
        </Suspense>
      )}
    </PlatformLayout>
  );
}